'use client';

import { useEffect, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Stack, Text } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SizeSelector, { SizeStock } from "./SizeSelector";
import { addToCartRequest } from "@/services/cart";
import { openDrawer } from "@/shared/store/drawer";
import { Product, ProductStock } from "@/types/product";
import ProductFavoriteButton from "./ProductFavoriteButton";
import CashbackBadge from "./CashbackBadge";
import { useTranslations } from "next-intl";
import { useAnalytics } from "@/hooks/useAnalytics";

export default function ProductCartActions({
  product,
  stocks,
  children,
}: {
  product: Product;
  stocks: SizeStock[];
    children?: React.ReactNode;
  }) {
  const { track } = useAnalytics();
  const t = useTranslations();
  const productId = product.id;

  const sizesRef = useRef<HTMLDivElement | null>(null);

  const [selectedSizeName, setSelectedSizeName] = useState<ProductStock['sizeName'] | null>(null);
  const [showSizeError, setShowSizeError] = useState(false);
  const hasSizes = stocks.some((s) => s.sizeName !== null);
  const needSelectSize = hasSizes && selectedSizeName === null;

  const isOutOfStock = !stocks.some(
    s => s.amount > 0
  );

  const isOutOfStockWithoutSizes =
    !hasSizes &&
    stocks.reduce((sum, s) => sum + s.amount, 0) <= 0;

  const isOutOfStockWithSizes =
    hasSizes &&
    !stocks.some(s => s.sizeName && s.amount > 0);

  const queryClient = useQueryClient();

  const addToCartMutation = useMutation({
    mutationFn: async () => {
      await addToCartRequest({ productId, sizeName: selectedSizeName, amount: 1 });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      openDrawer({ type: "main", tab: "cart" });
    },
  });

  useEffect(() => {
    if (!needSelectSize) {
      setShowSizeError(false);
    }
  }, [needSelectSize]);

  useEffect(() => {
    document.body.classList.add('footer-offset--lg');
    return () => {
      document.body.classList.remove('footer-offset--lg');
    };
  }, []);

  return (
    <>
      <Box ref={sizesRef}>
        <SizeSelector
          stocks={stocks}
          selected={selectedSizeName}
          onChange={setSelectedSizeName}
        />
      </Box>
      {showSizeError && (
        <Text color="red.500" textStyle="regular14">
          {t('select_size')}
        </Text>
      )}
      {children}
      <Stack
        position={{ base: "fixed", md: "unset" }}
        left={0}
        zIndex={{ base: 10, md: 0 }}
        bottom={0}
        gap={"8px"}
        width="100%"
        bg="white"
        boxShadow={{ base: "0 -2px 58px rgba(0,0,0,0.3)", md: "none" }}
        paddingBlock={{ base: "12px", md: "0" }}
        paddingInline={{ base: "10px", md: 0 }}
      >
        <CashbackBadge amount={5} />
        <ButtonGroup size={"xl"} gap="10px">
          <Button
            onClick={() => {
              if (addToCartMutation.isPending) return;

              // 🔕 товар БЕЗ размеров и без остатков → ничего не делаем
              if (isOutOfStockWithoutSizes) return;

              // ⚠️ товар С размерами, но всё распродано → идём дальше
              if (hasSizes && isOutOfStockWithSizes) {
                setShowSizeError(true); // или другой текст: t('out_of_stock')
                sizesRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
                return;
              }

              // ⚠️ размер обязателен, но не выбран
              if (hasSizes && needSelectSize) {
                setShowSizeError(true);
                sizesRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
                return;
              }

              setShowSizeError(false);
              track({
                type: 'add_to_cart',
                data: {
                  from: 'View Item',
                  product: { ...product, quantity: 1 },
                }
              });
              addToCartMutation.mutate();
            }}
            disabled={isOutOfStock}
            borderRadius={0}
            flex={1}
            height="56px"
          >
            <Text textStyle="bold16">{isOutOfStock ? t('sold_out') : t('add_to_cart')}</Text>
          </Button>
          <ProductFavoriteButton product={product} borderRadius={"none"} borderWidth={"1px"} borderColor={"gray.solid"}
            height={"56px"} width={"56px"} />
        </ButtonGroup>
      </Stack>
    </>
  );
}